import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { setToken, clearToken, getToken } from '../utils/api';

const UsersContext = createContext();

export const ROLES = {
  SUPER_USER: 'super_user',
  ACCOUNT_MANAGER: 'account_manager',
  EMPLOYER_CONTACT: 'employer_contact',
};

export const ROLE_LABELS = {
  super_user: 'Super User',
  account_manager: 'Account Manager',
  employer_contact: 'Employer Contact',
};

export function UsersProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = getToken();
      
      if (token) {
        try {
          // Fetch current user from backend
          const userData = await api.auth.me();
          setCurrentUser(userData);
        } catch (error) {
          console.error('Failed to restore session:', error);
          clearToken();
        }
      }
      
      setLoading(false);
    };

    restoreSession();
  }, []);

  // Login - calls backend API
  const login = async (email, password) => {
    try {
      const response = await api.auth.login(email, password);
      
      // Store JWT token
      setToken(response.access_token);
      
      // Check if password change required
      if (response.requiresPasswordChange) {
        return {
          success: true,
          requiresPasswordChange: true,
          user: response.user,
          isEmployerContact: response.isEmployerContact,
          clientId: response.clientId,
        };
      }
      
      // Set current user
      setCurrentUser(response.user);
      
      return {
        success: true,
        user: response.user,
        isEmployerContact: response.isEmployerContact,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed',
      };
    }
  };

  // Complete first login (set new password)
  const completeFirstLogin = async (userId, newPassword, isEmployerContact = false, clientId = null) => {
    try {
      const response = await api.auth.firstLogin(userId, newPassword, isEmployerContact, clientId);
      
      // Store new token
      setToken(response.access_token);
      
      // Set current user
      setCurrentUser(response.user);
      
      return true;
    } catch (error) {
      console.error('Failed to complete first login:', error);
      return false;
    }
  };

  // Change password
  const changePassword = async (newPassword) => {
    try {
      await api.auth.changePassword(newPassword);
      return true;
    } catch (error) {
      console.error('Failed to change password:', error);
      return false;
    }
  };

  // Logout
  const logout = () => {
    clearToken();
    setCurrentUser(null);
  };

  // Helper functions
  const isSuperUser = () => currentUser?.role === 'super_user';
  const isAccountManager = () => currentUser?.role === 'account_manager';
  const isEmployerContact = () => currentUser?.role === 'employer_contact';

  const getInitials = () => {
    if (!currentUser) return '?';
    return `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`.toUpperCase();
  };

  const getAvatarColor = () => {
    if (!currentUser) return '#999';
    const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444'];
    const hash = currentUser.email?.charCodeAt(0) || 0;
    return colors[hash % colors.length];
  };

  // Refresh current user data
  const refreshCurrentUser = async () => {
    try {
      const userData = await api.auth.me();
      setCurrentUser(userData);
      return true;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return false;
    }
  };

  // Load all users (Super User only)
  const loadUsers = async () => {
    if (!isSuperUser()) return;
    
    try {
      const usersData = await api.users.list();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // Invite user (Super User only)
  const inviteUser = async (userData, invitedBy) => {
    try {
      const response = await api.users.invite(userData);
      
      // Reload users list
      await loadUsers();
      
      return {
        success: response.success,
        tempPassword: response.tempPassword,
        user: response.user
      };
    } catch (error) {
      console.error('Failed to invite user:', error);
      return null;
    }
  };

  // Update user (Super User only)
  const updateUser = async (userId, updates) => {
    try {
      await api.users.update(userId, updates);
      
      // If updating current user, refresh their data
      if (userId === currentUser?.id) {
        await refreshCurrentUser();
      }
      
      // Reload users list
      await loadUsers();
      
      return true;
    } catch (error) {
      console.error('Failed to update user:', error);
      return false;
    }
  };

  // Deactivate user (Super User only)
  const deactivateUser = async (userId) => {
    try {
      await api.users.deactivate(userId);
      
      // Reload users list
      await loadUsers();
      
      return true;
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      return false;
    }
  };

  // Reactivate user (Super User only)
  const reactivateUser = async (userId) => {
    try {
      await api.users.reactivate(userId);
      
      // Reload users list
      await loadUsers();
      
      return true;
    } catch (error) {
      console.error('Failed to reactivate user:', error);
      return false;
    }
  };

  // Reset password (Super User only)
  const resetPassword = async (userId) => {
    try {
      const response = await api.users.resetPassword(userId);
      
      // Reload users list
      await loadUsers();
      
      return response.tempPassword;
    } catch (error) {
      console.error('Failed to reset password:', error);
      return null;
    }
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId) || null;
  };

  const getActiveUsers = () => {
    return users.filter(u => u.status === 'active');
  };

  const getUserStats = () => {
    return {
      total: users.length,
      active: users.filter(u => u.status === 'active').length,
      invited: users.filter(u => u.status === 'invited').length,
      deactivated: users.filter(u => u.status === 'deactivated').length,
    };
  };

  const value = {
    currentUser,
    loading,
    login,
    logout,
    completeFirstLogin,
    changePassword,
    isSuperUser,
    isAccountManager,
    isEmployerContact,
    getInitials,
    getAvatarColor,
    getUserById,
    getActiveUsers,
    getUserStats,
    users,
    loadUsers,
    inviteUser,
    updateUser,
    deactivateUser,
    reactivateUser,
    resetPassword,
    refreshCurrentUser,
  };

  return (
    <UsersContext.Provider value={value}>
      {children}
    </UsersContext.Provider>
  );
}

export function useUsers() {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers must be used within UsersProvider');
  }
  return context;
}
